import React, { useCallback, useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import {
  getApplications,
  selectApplications,
} from "../../models/applications/applicationsSlice";
import { useRequestStatus } from "../../models/asyncRequestStatusReducer";
import { useRouteMatch, useHistory, useLocation } from "react-router-dom";
import { selectOrg } from "../../models/organization/organizationSlice";
import {
  statusToTitle,
  formatMoney,
  formatDate,
  asyncTimeout,
  benevolenceStatusToDocumentStatus,
} from "../../utils/helpers";
import {
  Application,
  SortOptions,
} from "../../models/applications/applicationInterfaces";
import { TableColHeader } from "../../components/tableColHeader";
import { selectMyUser } from "../../models/users/myUserSlice";
import { GlobalFlashMessage } from "../../components/flashMessage";
import { systemMessageSlice } from "../../models/systemMessageSlice";
import { Spinner } from "../../components/spinner";
import Pagination from "../../components/pagination/pagination";
import Unauthorized from "../Unauthorized";
import { capitalizeFirstLetter } from "../../utils/formatting";
import { size } from "lodash";
import { Alert } from "antd";
interface DashboardProps {
  search: string;
  viewFilter: string;
  includeArchived: boolean;
}

export const Dashboard = ({
  search,
  viewFilter,
  includeArchived,
}: DashboardProps) => {
  // Hooks and selectors first
  const dispatch = useDispatch();
  let { path } = useRouteMatch();
  let history = useHistory();
  const location = useLocation();
  const organization = useSelector(selectOrg);
  const user = useSelector(selectMyUser);
  const myUser = useSelector(selectMyUser);
  const [isLoading] = useRequestStatus(getApplications.typePrefix);
  const { submissions, totalRecords, options } =
    useSelector(selectApplications);

  // Generate link to new view by removing the view query param
  const newViewLink =
    location.pathname +
    location.search.replace(/[?&]view=classic/, "").replace(/^&/, "?");

  const [activeFilter, setActiveFilter] = useState<SortOptions>({
    assignedTo: undefined,
    status: undefined,
    decisionStatus: undefined,
    includeArchived,
    offset: 0,
  });

  const fetchApplications = useCallback(
    (sortOptions?: SortOptions) => {
      return dispatch(getApplications(sortOptions || {}));
    },
    [dispatch]
  );

  // Build filter based on current props
  const buildFilter = useCallback((): SortOptions => {
    let filter: SortOptions = {
      includeArchived,
      offset: 0,
      search: search || undefined,
    };

    if (!viewFilter) {
      return {
        ...filter,
        assignedTo: undefined,
        status: undefined,
        decisionStatus: undefined,
      };
    } else if (viewFilter === "assignedToMe") {
      return {
        ...filter,
        assignedTo: myUser?.userId,
        status: undefined,
        decisionStatus: undefined,
      };
    } else {
      return {
        ...filter,
        assignedTo: undefined,
        ...(organization.meta?.enableDecisioning === true
          ? { status: undefined, decisionStatus: viewFilter }
          : { status: viewFilter, decisionStatus: undefined }),
      };
    }
  }, [
    viewFilter,
    search,
    includeArchived,
    myUser?.userId,
    organization.meta?.enableDecisioning,
  ]);

  // Update activeFilter and fetch when filters change
  useEffect(() => {
    const newFilter = buildFilter();
    setActiveFilter(newFilter);
    fetchApplications(newFilter);
  }, [buildFilter, fetchApplications]);

  const headerCols = [
    { title: "Application #", value: "id" },
    {
      title: "Assigned To",
      value: "assigned_to",
    },
    { title: "First Name", value: "first_name" },
    { title: "Last Name", value: "last_name" },
    {
      title: `${"Workflow"}`,
      value: "category",
    },
    { title: "Request Amount", value: "amount_requested" },
    { title: "Received", value: "created_at" },
    { title: "Last Activity", value: "last_activity" },
    { title: "Status", value: "status" },
    { title: "Status", value: "decision_status" },
  ];

  function hasRecentActivity(sub: Application) {
    const lastViewed = sub.lastViewed ? new Date(sub.lastActivity) : null;
    const lastActivity = sub.lastActivity ? new Date(sub.lastActivity) : null;
    if (!lastActivity) return false;
    if (!lastViewed) return true;
    if (lastActivity.toUTCString() > lastViewed.toUTCString()) return true;
    return false;
  }

  const getAmountRequested = (sub: any) => {
    // If an amount requested is not provided, return dashes
    if (!sub) return "--------";
    const formattedRequest = formatMoney(sub.amountRequested);
    // If the amount requested is zero, return dashes
    if (formattedRequest === "$0.00") return "--------";
    // Else return the formatted amount
    return formattedRequest;
  };
  if (!user) {
    return null;
  }

  return (
    <>
      <GlobalFlashMessage />

      {/* Info banner about the new view */}
      <Alert
        message={
          <span>
            <strong>Using Classic View.</strong> For advanced filtering and
            sorting, try the{" "}
            <a
              href={newViewLink}
              style={{ textDecoration: "underline", color: "inherit" }}
            >
              New View
            </a>{" "}
            (limited to 15,000 records).
          </span>
        }
        type="info"
        banner
        closable
        style={{ marginBottom: "10px" }}
      />

      <div
        className="application-table"
        style={{ position: "relative", minHeight: 600 }}
      >
        <Spinner show={isLoading} />

        {/* Dashboard Table */}
        {!isLoading && submissions.length > 0 ? (
          <>
            <table className="table table-borderless table-striped table-hover">
              <thead className="thead-dark">
                <tr>
                  <th scope="col"></th>
                  {headerCols
                    .filter(
                      ({ value: v }) =>
                        v !==
                        (organization.meta?.enableDecisioning === true
                          ? "status"
                          : "decision_status")
                    )
                    .map((h) => (
                      <TableColHeader
                        title={h.title}
                        key={h.value}
                        sort={
                          options.sort === h.value
                            ? options.direction
                            : undefined
                        }
                        onClick={(direction: "asc" | "desc") => {
                          fetchApplications({
                            sort: h.value,
                            direction: direction,
                          });
                        }}
                      />
                    ))}
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((sub) => {
                  return (
                    <tr
                      key={sub.id}
                      onClick={() => {
                        history.push(`applications/${sub.id}/activity`);
                      }}
                    >
                      <td className="new-cta">
                        {hasRecentActivity(sub) && (
                          <span className="oi oi-media-record"></span>
                        )}
                      </td>
                      <td>{sub.id}</td>
                      <td>
                        {sub.benevolenceAdmin.firstName +
                          " " +
                          sub.benevolenceAdmin.lastName}
                      </td>
                      <td>
                        {capitalizeFirstLetter(
                          sub.applicant?.firstName || sub.user?.firstName
                        )}
                      </td>
                      <td>
                        {capitalizeFirstLetter(
                          sub.applicant?.lastName || sub.user?.lastName
                        )}
                      </td>
                      <td>{capitalizeFirstLetter(sub.category)}</td>
                      <td>{getAmountRequested(sub)}</td>
                      {/* TODO: Fix backend so that this can be createdAt */}
                      <td>{formatDate(sub.created_at!) || ""}</td>
                      <td>{formatDate(sub.lastActivity) || "N/A"}</td>
                      {/* For organizations with decisioning turned on (default), use the decision status table to reflect submission status */}
                      {organization.meta?.enableDecisioning === true ? (
                        <>
                          <td
                            onClick={() => {
                              history.push(`applications/${sub.id}/activity`);
                            }}
                          >
                            <div>{sub.decisionStatus?.title || "-"}</div>
                            {sub.archived && (
                              <div
                                className="text-primary text-uppercase"
                                style={{ fontSize: "8px" }}
                              >
                                Archived
                              </div>
                            )}
                          </td>
                        </>
                      ) : (
                        /* For legacy clients, use the document status table to reflect submission status */
                        <td
                          onClick={() => {
                            history.push(`applications/${sub.id}/activity`);
                          }}
                        >
                          {sub.archived && (
                            <div
                              className="text-primary text-uppercase"
                              style={{ fontSize: "8px" }}
                            >
                              Archived
                            </div>
                          )}

                          <div>{statusToTitle[sub.status]}</div>
                        </td>
                      )}{" "}
                      <td className="cta-arrow">
                        <span className="oi oi-chevron-right"></span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {/* Footer */}
            <div className="d-flex justify-content-between px-5 pt-3 align-items-center">
              <Pagination
                sortOptions={options}
                loading={isLoading}
                totalRecords={totalRecords}
                fetch={fetchApplications}
              />
            </div>
          </>
        ) : (
          !isLoading && (
            <p className="d-block text-center mt-5 h5 font-italic text-muted">
              No entries found
            </p>
          )
        )}
      </div>
    </>
  );
};
